import React, { useContext, useMemo, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import classNames from 'classnames';

import { IdName } from 'interfaces/IdName';
import {
  I18N_AVANT_PROPERTY_COMMON_LABEL_PATH,
  I18N_PLATFORM_COMMON_WORD_PATH,
} from 'constants/i18n';
import { PropertyTypeNames, PropertyTypes } from 'constants/propertyTypes';
import { PropertySubtypes } from 'constants/propertySubtypes';
import { PropertyClass } from 'constants/propertyClass';
import { BUILDING_CLASSES_OPTIONS } from 'constants/buildingClasses';
import { authContext, useUserAccessData } from 'contexts/AuthContext';
import { useUserMarkets } from 'hooks/useUserMarkets';
import { translateText } from 'utils/i18n';

import { MenuDropdown } from './Components/MenuDropdown';
import {
  getCompPropertiesLinks,
  getMappingLinks,
  getMarketAnalyticsLinks,
  getSubmarketList,
} from './utils';
import styles from './SubNavBar.module.scss';
import { useNavBarData } from './useNavBarData';

const SubNavBar = () => {
  const { user } = useContext(authContext);
  const history = useHistory();
  const { fullAccess, accessData } = useUserAccessData();
  const { userMarkets } = useUserMarkets(user, true);

  const [userPreferredType, setUserPreferredType] = useState<IdName | null>(
    null,
  );

  const onCompleteLoadTypes = (types: IdName[]) => {
    const userPreferredType = types.find(
      type =>
        user.preferences?.propertyType &&
        PropertyTypeNames[type?.id] === user.preferences.propertyType,
    );
    if (!userPreferredType?.id) return;
    setUserPreferredType(userPreferredType);
  };

  const isIndustrialFlex = [
    PropertyTypes.flexIndustrial,
    PropertyTypes.industrial,
  ].includes(userPreferredType?.name);

  const { data, handlers, loading } = useNavBarData({
    isIndustrialFlex,
    onCompleteLoadTypes,
  });

  const propertyClasses = useMemo(() => {
    if (isIndustrialFlex) return [];

    return (
      data.classes
        .filter(c => BUILDING_CLASSES_OPTIONS.includes(c.name))
        .map(cn =>
          cn.name !== PropertyClass.Trophy
            ? { ...cn, name: `Class ${cn.name}` }
            : cn,
        ) ?? []
    );
  }, [data.classes, isIndustrialFlex]);

  const subTypes = useMemo(() => {
    if (!isIndustrialFlex) return [];

    return (
      data.subTypes.filter(subType => {
        if (subType.name === PropertySubtypes.filmStudio) return;

        const transformedType = userPreferredType?.name
          ?.replaceAll(' ', '')
          .toUpperCase();
        return subType.type === transformedType;
      }) ?? []
    );
  }, [data.subTypes, isIndustrialFlex, userPreferredType?.name]);

  const analyticsDashboardsId = accessData?.analyticsDashboardsId || [];

  const renderMarketMenuHeader = () => {
    return (
      <div className={styles['market-dropdown-content']}>
        {data.initialListMarket.map(market => (
          <button
            key={market.id}
            className={classNames(styles['market-dropdown-button'], {
              [styles['selected']]: data.currentMarket === market,
            })}
            onMouseOver={() => handlers.handleHoverMarket(market)}
            onClick={() => {
              handlers.handleClickMarket(market);
            }}
          >
            {market.name}
          </button>
        ))}
      </div>
    );
  };

  return (
    <div className={styles['menu-container']}>
      <div className={classNames(styles['menu-item'], styles['activity'])}>
        <Link to={handlers.onNewsClickHandle} className={styles['menu-link']}>
          {translateText(`${I18N_PLATFORM_COMMON_WORD_PATH}.news`)}
        </Link>
      </div>
      <div className={styles['menu-item']}>
        <MenuDropdown
          id="subnav-market-overview"
          label={translateText(
            `${I18N_PLATFORM_COMMON_WORD_PATH}.market_plural`,
          )}
          gridClassName={styles['market-dropdown-container']}
          listItems={getSubmarketList(data.submarkets, history)}
          customHeader={renderMarketMenuHeader()}
          isLoading={
            loading.submarketsDataLoading || loading.userMarketDataLoading
          }
        />
      </div>
      <div className={styles['menu-item']}>
        <MenuDropdown
          id="subnav-comps-dropdown"
          label={translateText(`${I18N_PLATFORM_COMMON_WORD_PATH}.data`)}
          listItems={getCompPropertiesLinks(
            user,
            userMarkets,
            userPreferredType,
            subTypes,
            propertyClasses,
            history,
          )}
          isLoading={
            loading.propertyTypesDataLoading ||
            loading.subTypesDataLoading ||
            loading.classesDataLoading
          }
        />
      </div>
      <div className={styles['menu-item']}>
        <MenuDropdown
          id="subnav-market-dropdown"
          label={translateText(`${I18N_PLATFORM_COMMON_WORD_PATH}.analytics`)}
          listItems={getMarketAnalyticsLinks(
            history,
            data.analytics,
            analyticsDashboardsId,
            fullAccess,
          )}
          gridClassName={styles['market-analytics-dropdown']}
          isLoading={loading.dynamicUrlsDataLoading}
        />
      </div>
      <div className={styles['menu-item']}>
        <MenuDropdown
          id="subnav-mapping"
          label={translateText(
            `${I18N_AVANT_PROPERTY_COMMON_LABEL_PATH}.products`,
          )}
          listItems={getMappingLinks()}
          gridClassName={styles['mapping-dropdown']}
        />
      </div>
    </div>
  );
};

export default SubNavBar;
